/*
 * REVIEWS.io polaris widget mount + custom breakdown/toolbar layer — the
 * logic formerly inlined by snippets/reviewsio-widget.liquid (live's reviews
 * snippet, D-19.4), moved here so it is cached and parsed once per page
 * instead of emitted as ~48 KB of inline script per widget (perf audit
 * 2026-09-26). Behaviour is unchanged; the Liquid values it used arrive per
 * widget as JSON in <script type="application/json" data-reviewsio-config>.
 *
 * Config keys: id, lang, sku, grouping, translations,
 * filterTranslations, checkmarkUrl, lazyModal (optional <modal-dialog> id:
 * mount on its first open instead of when the widget approaches), designMode
 * (request.design_mode: load polaris at once and mount on DOMContentLoaded).
 *
 * Rich snippets are off (D-37): with enable_rich_snippets polaris injects its
 * own Product JSON-LD (#reviewsWidgetProductSnippets) and, with Q&A
 * structured data, an FAQPage (#reviewsWidgetFaqSnippets) into <head> after
 * consent, a second Product / FAQPage next to the layout's
 * (snippets/json-ld). The Q&A switch is set explicitly as well.
 *
 * polaris/build.js (~97 KB brotli; at construction it adds the widget's own
 * stylesheets, elements-widget.css + the icon font CSS) is no longer a static
 * tag on every PDP (perf round 3). loadPolaris() requests it once per page
 * when the first widget is due to mount: a strip widget when it comes within
 * ~1.5 viewports of the visible area, the popup copy when its modal opens.
 */
(function () {
  const POLARIS_SRC = 'https://widget.reviews.io/polaris/build.js';

  // One shared request per page. The promise lives on window because this
  // file runs once per rendered widget (each render emits its <script> tag).
  // A dynamically inserted script never blocks the parser; inserting it only
  // once the document is parsed makes it run no earlier than `defer` would.
  // A script that errors is removed and the slot freed, so the next trigger
  // retries. A consent-blocked script settles neither way: the mount's
  // bounded retry and script watcher (initReviewsWidget) cover that case.
  const loadPolaris = () => {
    if (window.__reviewsIoPolarisLoad) return window.__reviewsIoPolarisLoad;
    const load = new Promise((resolve, reject) => {
      if (typeof window.ReviewsWidget === 'function') {
        resolve();
        return;
      }
      const existing = document.querySelector('script[src^="' + POLARIS_SRC + '"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('polaris failed to load')), { once: true });
        return;
      }
      const inject = () => {
        const script = document.createElement('script');
        script.src = POLARIS_SRC;
        script.addEventListener('load', () => resolve(), { once: true });
        script.addEventListener(
          'error',
          () => {
            script.remove();
            reject(new Error('polaris failed to load'));
          },
          { once: true }
        );
        document.head.appendChild(script);
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', inject, { once: true });
      } else {
        inject();
      }
    });
    window.__reviewsIoPolarisLoad = load;
    load.catch(() => {
      if (window.__reviewsIoPolarisLoad === load) window.__reviewsIoPolarisLoad = null;
    });
    return load;
  };

  // Calls back once, when the element comes within ~1.5 viewports of the
  // visible area (above or below). Without IntersectionObserver it calls back
  // at once, i.e. the widget mounts as it did before.
  const whenApproaching = (element, callback) => {
    if (typeof window.IntersectionObserver !== 'function') {
      callback();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        callback();
      },
      { rootMargin: '150% 0px' }
    );
    observer.observe(element);
  };

  function initReviewsIo(config) {
    const widgetSelector = '#' + config.id;
    const designMode = config.designMode === true || Boolean(window.Shopify && window.Shopify.designMode);
    // Theme editor: request polaris immediately, as the static tag did.
    if (designMode) loadPolaris();
    // Translations for the custom filter UI and breakdown (Shopify locale, via the config).
    window.__reviewsIoFilterTranslations = config.filterTranslations;
    (function () {
    const initReviewsWidget = () => {
        // Every mount path (strip on approach, popup on open, theme editor)
        // requests polaris first; the request is shared by all widgets.
        const polarisLoad = loadPolaris();
        const getWidgetContainer = () => document.querySelector(widgetSelector);

        // Prevent duplicate initialization
        const widgetContainer = getWidgetContainer();
        if (widgetContainer && widgetContainer.dataset.initialized === 'true') {
            return;
        }

        const mountReviewsWidget = () => {
            const currentWidgetContainer = getWidgetContainer();

            if (!currentWidgetContainer) {
                return false;
            }

            if (currentWidgetContainer.dataset.initialized === 'true') {
                return true;
            }

            if (typeof window.ReviewsWidget !== 'function') {
                return false;
            }

            try {
                new ReviewsWidget(widgetSelector, {
            store: 'www.sheko.com',
            widget: 'polaris',
            //Content settings (store_review,product_review,third_party_review,questions). Choose what to display in this widget:
            options: {
                types: 'product_review',
                lang: config.lang,
                //Possible layout options: bordered, large, reverse, and expanded (for visible filters).
                layout: 'expanded',
                //How many reviews & questions to show per page?
                per_page: 4,
                store_review: { hide_if_no_results: true },
                third_party_review: { hide_if_no_results: true },
                product_review: {
                    min_rating: 3,
                    sku: config.sku,
                    enable_rich_snippets: false,
                    tag: config.lang,
                },
//Toggle Options
                questions: {
                    hide_if_no_results: false,
                    enable_ask_question: true,
                    show_dates: true,
                    grouping: config.grouping,
                    include_qna_structured_data: false
                },
                //Header settings:
                header: {
                    enable_summary: true, //Show overall rating & review count
                    enable_ratings: true,
                    enable_breakdown: true, //Show star rating breakdown bars
                    enable_attributes: true,
                    enable_image_gallery: true, //Show photo & video gallery
                    enable_percent_recommended: false, //Show what percentage of reviewers recommend it
                    enable_write_review: false, //Show "Write Review" button
                    enable_ask_question: false, //Show "Ask Question" button
                    enable_sub_header: true, //Show subheader (required for expanded layout filters)
                    rating_decimal_places: 2,
                },
                //Filtering settings:
                filtering: {
                    enable: true, //Show filtering options
                    enable_text_search: true, //Show search field
                    enable_sorting: true, //Show sorting options (most recent, most popular)
                    enable_product_filter: false, //Show product options filter
                    enable_media_filter: false, //Show reviews with images/video/media options
                    enable_overall_rating_filter: true, //Show overall rating breakdown filter
                    enable_language_filter: false, //Filter by review language
                    enable_language_filter_language_change: false, //Update widget language based on language selected
                    enable_ratings_filters: false, //Show product attributes filter
                    enable_attributes_filters: false, //Show author attributes filter
                },
                //Review settings:
                reviews: {
                    enable_avatar: false, //Show author avatar
                    enable_reviewer_name: true, //Show author name
                    enable_reviewer_address: false, //Show author location
                    reviewer_address_format: 'city, country', //Author location display format
                    enable_verified_badge: true, //Show "Verified Customer" badge
                    enable_subscriber_badge: true, //Show "Verified Subscriber" badge
                    review_content_filter: '', //Filter content
                    enable_reviewer_recommends: false, //Show "I recommend it" badge
                    enable_attributes: false, //Show author attributes
                    enable_product_name: true, //Show display product name
                    enable_review_title: false, //Show review title
                    enable_replies: undefined, //Show review replies
                    enable_images: true, //Show display review photos
                    enable_ratings: true, //Show product attributes (additional ratings)
                    enable_share: false, //Show share buttons
                    enable_helpful_vote: false, //Show "was this helpful?" section
                    enable_helpful_display: false, //Show how many times times review upvoted
                    enable_report: false, //Show report button
                    enable_date: true, //Show when review was published
                    enable_third_party_source: true, //Show third party source
                },
            },
            translations: config.translations,
            styles: {
                '--base-font-size': '16px',
                '--common-button-font-family': 'inter',
                '--common-button-font-size': '16px',
                '--common-button-font-weight': '500',
                '--common-button-letter-spacing': '0',
                '--common-button-text-transform': 'none',
                '--common-button-vertical-padding': '10px',
                '--common-button-horizontal-padding': '20px',
                '--common-button-border-width': '2px',
                '--common-button-border-radius': '0px',
                '--primary-button-bg-color': '#FF755A',
                '--primary-button-border-color': '#FF755A',
                '--primary-button-text-color': '#ffffff',
                '--secondary-button-bg-color': 'transparent',
                '--secondary-button-border-color': '#FF755A',
                '--secondary-button-text-color': '#FF755A',
                '--common-star-color': '#FFD21D',
                '--common-star-disabled-color': 'transparent',
                '--medium-star-size': '25px',
                '--small-star-size': '21px',
                '--heading-text-color': '#0E1311',
                '--heading-text-font-weight': '600',
                '--heading-text-font-family': 'inter',
                '--heading-text-line-height': '2',
                '--heading-text-letter-spacing': '0',
                '--heading-text-transform': 'none',
                '--body-text-color': '#767676',
                '--body-text-font-weight': '400',
                '--body-text-font-family': 'inter',
                '--body-text-line-height': '1,5',
                '--body-text-letter-spacing': '0',
                '--body-text-transform': 'none',
                '--inputfield-text-font-family': 'inter',
                '--input-text-font-size': '15px',
                '--inputfield-text-font-weight': '400',
                '--inputfield-text-color': '#32323A',
                '--inputfield-border-color': 'rgba(0,0,0,0.2)',
                '--inputfield-background-color': 'transparent',
                '--inputfield-border-width': '1px',
                '--inputfield-border-radius': '0px',
                '--common-border-color': 'rgba(0,0,0,0.15)',
                '--common-border-width': '1px',
                '--common-sidebar-width': '190px',
                '--slider-indicator-bg-color': 'rgba(0,0,0,0.1)',
                '--slider-indicator-button-color': '#0E1311',
                '--slider-indicator-width': '190px',
                '--badge-icon-color': '#097331',
                '--badge-icon-font-size': 'inherit',
                '--badge-text-color': '#097331',
                '--badge-text-font-size': 'inherit',
                '--badge-text-letter-spacing': 'inherit',
                '--badge-text-transform': 'inherit',
                '--author-font-size': 'inherit',
                '--author-text-transform': 'none',
                '--avatar-thumbnail-size': '60px',
                '--avatar-thumbnail-border-radius': '100px',
                '--avatar-thumbnail-text-color': '#32323A',
                '--avatar-thumbnail-bg-color': 'rgba(0,0,0,0.1)',
                '--photo-video-thumbnail-size': '80px',
                '--photo-video-thumbnail-border-radius': '12px',
                '--mediaslider-scroll-button-icon-color': '#0E1311',
                '--mediaslider-scroll-button-bg-color': 'rgba(255, 255, 255, 0.85)',
                '--mediaslider-overlay-text-color': '#ffffff',
                '--mediaslider-overlay-bg-color': 'rgba(0, 0, 0, 0.8))',
                '--mediaslider-item-size': '110px',
                '--pagination-tab-text-color': '#767676',
                '--pagination-tab-text-transform': 'none',
                '--pagination-tab-text-letter-spacing': '0',
                '--pagination-tab-text-font-size': '16px',
                '--pagination-tab-text-font-weight': '600',
                '--pagination-tab-active-text-color': '#32323A',
                '--pagination-tab-active-text-font-weight': '600',
                '--pagination-tab-active-border-color': '#32323A',
                '--pagination-tab-border-width': '2px',
            },
                });

                if (currentWidgetContainer) {
                    currentWidgetContainer.dataset.initialized = 'true';
                }

                return true;
            } catch (error) {
                console.error('Reviews.io widget initialization failed', error);
                return false;
            }
        };

        let retryCount = 0;
        let retryTimer;
        const maxRetryCount = 300;
        let postMountInitDone = false;

        const stopRetrying = () => {
            if (retryTimer) {
                clearInterval(retryTimer);
                retryTimer = null;
            }
        };

        // The polaris script can arrive long after page load: the consent
        // manager (Pandectes) blocks widget.reviews.io until the visitor
        // accepts, then re-injects the script. Toolbar/breakdown setup must
        // therefore start from MOUNT SUCCESS, not from timers anchored to
        // DOMContentLoaded (those windows are dead by the time a late
        // consent lands, leaving reviews-breakdown__inner empty).
        const runPostMountInit = () => {
            if (postMountInitDone) return;
            postMountInitDone = true;
            waitForFilters();
            waitForBreakdown();
        };

        // After the bounded retry gives up, watch for the polaris script
        // being (re)injected — e.g. by the consent manager on accept — and
        // restart the bounded retry. CMP-agnostic and self-disconnecting.
        let scriptWatcher;
        const armScriptWatcher = () => {
            if (scriptWatcher || typeof MutationObserver === 'undefined') return;
            scriptWatcher = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    for (const node of mutation.addedNodes) {
                        if (node.tagName === 'SCRIPT' && (node.src || '').indexOf('widget.reviews.io') !== -1) {
                            scriptWatcher.disconnect();
                            scriptWatcher = null;
                            retryCount = 0;
                            ensureWidgetMounted();
                            return;
                        }
                    }
                }
            });
            scriptWatcher.observe(document.documentElement, { childList: true, subtree: true });
        };

        const ensureWidgetMounted = () => {
            if (mountReviewsWidget()) {
                stopRetrying();
                runPostMountInit();
                return true;
            }

            if (!retryTimer) {
                retryTimer = setInterval(() => {
                    retryCount += 1;
                    if (mountReviewsWidget()) {
                        stopRetrying();
                        runPostMountInit();
                    } else if (retryCount >= maxRetryCount) {
                        stopRetrying();
                        armScriptWatcher();
                    }
                }, 200);
            }

            return false;
        };

        const syncBreakdownVisibility = () => {
            const widgetRoot = document.querySelector(widgetSelector);
            if (!widgetRoot) return;

            const scoreEl = widgetRoot.querySelector('.reviews-breakdown__score');
            if (!scoreEl || scoreEl.textContent.trim() === '') return;
            if (!widgetRoot.classList.contains('reviews-toolbar-ready')) return;

            widgetRoot.classList.add('reviews-breakdown-ready');
        };

        const initCustomToolbar = () => {
            const T = window.__reviewsIoFilterTranslations || {};
            const widgetRoot = document.querySelector(widgetSelector);
            if (!widgetRoot) return false;

            const originalDropdown = widgetRoot.querySelector('.R-DropUpDownMenu');
            const nativeSubheader = widgetRoot.querySelector('.ElementsWidget__subheader');
            const nativeSearchWrapper = widgetRoot.querySelector('.c-searchFilters__search, .ElementsWidget__search');
            const nativeSearchInput = widgetRoot.querySelector('.c-searchFilters__search input, .ElementsWidget__search input, .ElementsWidget__search .R-Field__input, .c-searchFilters input');
            const insertionPoint = widgetRoot.querySelector('.ElementsWidget__media, .R-ContentList, .ElementsWidget__tabs, .ElementsWidget__content');

            if (!insertionPoint) return false;

            if (!originalDropdown) return false;

            const allItems = originalDropdown.querySelectorAll('.R-DropUpDownMenu__item, [role="option"], .c-dropdown__group__heading, .c-filters__dropdown__clearButton');
            if (!allItems.length) return false;

            const sortItemsOriginal = [];
            const ratingItemsOriginal = [];

            allItems.forEach((item) => {
                const hasStars = item.querySelector('.R-RatingStars');
                const isHeader = item.classList.contains('c-dropdown__group__heading');
                const itemText = item.textContent.toLowerCase();
                const isClearButton =
                    item.classList.contains('c-filters__dropdown__clearButton') ||
                    itemText.includes('clear') ||
                    itemText.includes((T.clear || 'zurücksetzen').toLowerCase());

                if (isHeader || isClearButton) return;

                if (hasStars) {
                    ratingItemsOriginal.push(item);
                } else {
                    sortItemsOriginal.push(item);
                }
            });

            if (!sortItemsOriginal.length && !ratingItemsOriginal.length) return false;

            let toolbar = widgetRoot.querySelector('.reviews-toolbar');
            if (!toolbar) {
                toolbar = document.createElement('div');
                toolbar.className = 'reviews-toolbar';

                const searchWrapper = document.createElement('div');
                searchWrapper.className = 'reviews-search';

                const searchInput = document.createElement('input');
                searchInput.type = 'search';
                searchInput.className = 'reviews-search__input';
                searchInput.setAttribute('aria-label', 'Search reviews');
                searchWrapper.appendChild(searchInput);

                const filtersWrapper = document.createElement('div');
                filtersWrapper.className = 'reviews-toolbar__filters';

                toolbar.appendChild(searchWrapper);
                toolbar.appendChild(filtersWrapper);
                insertionPoint.parentNode.insertBefore(toolbar, insertionPoint);
            }

            const searchInput = toolbar.querySelector('.reviews-search__input');
            const filtersWrapper = toolbar.querySelector('.reviews-toolbar__filters');

            if (nativeSearchInput && !searchInput.dataset.boundNative) {
                searchInput.placeholder = nativeSearchInput.getAttribute('placeholder') || 'Search';
                searchInput.addEventListener('input', () => {
                    nativeSearchInput.value = searchInput.value;
                    ['input', 'change', 'keyup'].forEach((eventName) => {
                        nativeSearchInput.dispatchEvent(new Event(eventName, { bubbles: true }));
                    });
                });
                searchInput.dataset.boundNative = 'true';
            } else if (!nativeSearchInput && !searchInput.dataset.boundLocal) {
                searchInput.placeholder = 'Search';
                searchInput.addEventListener('input', () => {
                    const query = searchInput.value.trim().toLowerCase();
                    widgetRoot.querySelectorAll('.R-ContentList__item').forEach((item) => {
                        item.style.display = !query || item.textContent.toLowerCase().includes(query) ? '' : 'none';
                    });
                });
                searchInput.dataset.boundLocal = 'true';
            }

            if (filtersWrapper.querySelector('.reviews-sort-filter')) {
                if (nativeSearchWrapper) nativeSearchWrapper.classList.add('reviews-native-filter-hidden');
                if (originalDropdown) originalDropdown.classList.add('reviews-native-filter-hidden');
                widgetRoot.classList.add('reviews-toolbar-ready');
                syncBreakdownVisibility();
                return true;
            }

            const createCustomItem = (text) => {
                const item = document.createElement('div');
                item.className = 'reviews-dropdown-item';
                item.innerHTML = `<span>${text}</span><span class="reviews-checkmark"><img src="${config.checkmarkUrl}" alt="Selected" width="20" height="20"></span>`;
                return item;
            };

                filtersWrapper.insertAdjacentHTML(
                'beforeend',
                        `
                    <div class="reviews-filter-container reviews-sort-filter">
                        <div class="c-filters__triggerButton" tabindex="0" role="button">
                            ${T.sortNewest || 'Neueste'} <span class="ricon-thin-arrow--down u-verticalAlign--middle"></span>
                        </div>
                        <div class="reviews-custom-dropdown"></div>
                    </div>
                    <div class="reviews-filter-container reviews-rating-filter">
                        <div class="c-filters__triggerButton" tabindex="0" role="button">
                            ${T.allReviews || 'Alle Bewertungen'} <span class="ricon-thin-arrow--down u-verticalAlign--middle"></span>
                        </div>
                        <div class="reviews-custom-dropdown"></div>
                    </div>
                `
            );

            const sortFilter = filtersWrapper.querySelector('.reviews-sort-filter');
            const ratingFilter = filtersWrapper.querySelector('.reviews-rating-filter');
            const sortButton = sortFilter.querySelector('.c-filters__triggerButton');
            const ratingButton = ratingFilter.querySelector('.c-filters__triggerButton');
            const sortDropdown = sortFilter.querySelector('.reviews-custom-dropdown');
            const ratingDropdown = ratingFilter.querySelector('.reviews-custom-dropdown');

            [T.sortNewest || 'Neueste', T.sortOldest || 'Älteste', T.sortHelpful || 'Hilfreichste'].forEach((text, index) => {
                const item = createCustomItem(text);
                item.dataset.originalIndex = index;
                if (index === 0) item.classList.add('active');
                sortDropdown.appendChild(item);
            });

            const resetItem = createCustomItem(T.allReviews || 'Alle Bewertungen');
            resetItem.dataset.originalIndex = '-2';
            resetItem.dataset.isClearFilter = 'true';
            ratingDropdown.appendChild(resetItem);

            [T.stars5 || '5 Sterne', T.stars4 || '4 Sterne', T.stars3 || '3 Sterne', T.stars2 || '2 Sterne', T.stars1 || '1 Stern'].forEach((text, index) => {
                const item = createCustomItem(text);
                item.dataset.originalIndex = index;
                ratingDropdown.appendChild(item);
            });

            let lastActiveRatingIndex = -1;

            const closeAllDropdowns = () => {
                sortDropdown.classList.remove('is-open');
                ratingDropdown.classList.remove('is-open');
            };

            const toggleDropdown = (dropdown, otherDropdown) => {
                const isVisible = dropdown.classList.contains('is-open');
                dropdown.classList.toggle('is-open', !isVisible);
                otherDropdown.classList.remove('is-open');
            };

            const setActiveItem = (dropdown, item) => {
                dropdown.querySelectorAll('.reviews-dropdown-item').forEach((dropdownItem) => dropdownItem.classList.remove('active'));
                item.classList.add('active');
            };

            const updateButtonText = (button, text) => {
                button.innerHTML = `${text} <span class="ricon-thin-arrow--down u-verticalAlign--middle"></span>`;
            };

            sortButton.addEventListener('click', (event) => {
                event.stopPropagation();
                toggleDropdown(sortDropdown, ratingDropdown);
            });

            ratingButton.addEventListener('click', (event) => {
                event.stopPropagation();
                toggleDropdown(ratingDropdown, sortDropdown);
            });

            sortDropdown.addEventListener('click', (event) => {
                const customItem = event.target.closest('.reviews-dropdown-item');
                if (!customItem) return;

                const index = parseInt(customItem.dataset.originalIndex, 10);
                const text = customItem.querySelector('span').textContent;

                setActiveItem(sortDropdown, customItem);
                updateButtonText(sortButton, text);
                sortDropdown.classList.remove('is-open');

                const originalItem = sortItemsOriginal[index];
                if (originalItem) originalItem.click();
            });

            ratingDropdown.addEventListener('click', (event) => {
                const customItem = event.target.closest('.reviews-dropdown-item');
                if (!customItem) return;

                const index = parseInt(customItem.dataset.originalIndex, 10);
                const text = customItem.querySelector('span').textContent;
                const isClearFilter = customItem.dataset.isClearFilter === 'true';

                ratingDropdown.classList.remove('is-open');

                if (isClearFilter) {
                    const originalClearBtn = originalDropdown.querySelector('.c-filters__dropdown__clearButton [role="button"]');
                    if (originalClearBtn) {
                        originalClearBtn.click();
                    } else if (lastActiveRatingIndex >= 0) {
                        const originalItem = ratingItemsOriginal[lastActiveRatingIndex];
                        if (originalItem) originalItem.click();
                    }

                    setActiveItem(ratingDropdown, customItem);
                    lastActiveRatingIndex = -1;
                    updateButtonText(ratingButton, T.allReviews || 'Alle Bewertungen');
                    return;
                }

                setActiveItem(ratingDropdown, customItem);
                updateButtonText(ratingButton, text);

                if (lastActiveRatingIndex >= 0 && lastActiveRatingIndex !== index) {
                    const previousItem = ratingItemsOriginal[lastActiveRatingIndex];
                    if (previousItem) previousItem.click();
                }

                const originalItem = ratingItemsOriginal[index];
                if (originalItem) {
                    originalItem.click();
                    lastActiveRatingIndex = index;
                }
            });

            document.addEventListener('click', closeAllDropdowns);

            if (nativeSearchWrapper) nativeSearchWrapper.classList.add('reviews-native-filter-hidden');
            originalDropdown.classList.add('reviews-native-filter-hidden');
            widgetRoot.classList.add('reviews-toolbar-ready');
            syncBreakdownVisibility();
            return true;
        };

        const waitForFilters = () => {
            setTimeout(() => {
                if (initCustomToolbar()) return;

                const observer = new MutationObserver((mutations, obs) => {
                    for (let mutation of mutations) {
                        if (!mutation.addedNodes.length) continue;

                        if (initCustomToolbar()) {
                            obs.disconnect();
                            return;
                        }
                    }
                });

                const widgetContainer = document.querySelector(widgetSelector);
                if (widgetContainer) {
                    observer.observe(widgetContainer, { childList: true, subtree: true });
                    setTimeout(() => {
                        if (initCustomToolbar()) observer.disconnect();
                    }, 3000);
                }
            }, 100);
        };

        const initRatingBreakdown = () => {
            const widgetRoot = document.querySelector(widgetSelector);
            const breakdownContainer = document.querySelector(widgetSelector + '-breakdown');
            const headerLeft = document.querySelector(widgetSelector + ' .header__group--left');

            if (!widgetRoot) return false;
            if (!breakdownContainer) return false;
            if (!headerLeft) return false;
            const ratingEl = headerLeft.querySelector('.R-TextHeading');
            const countEl = headerLeft.querySelector('.R-TextBody');
            const starsEl = headerLeft.querySelector('.R-RatingStars');
            const scoreEl = breakdownContainer.querySelector('.reviews-breakdown__score');
            if (scoreEl && scoreEl.textContent.trim() !== '') {
                syncBreakdownVisibility();
                return true;
            }
            if (!ratingEl || !countEl) return false;

            const rating = ratingEl.textContent.trim();
            const countText = countEl.textContent.trim();
            const countMatch = countText.match(/[\d.,]+/);
            const totalCount = countMatch ? parseInt(countMatch[0].replace(/[.,]/g, '')) : 0;

            if (rating && countText) {
                const T = window.__reviewsIoFilterTranslations || {};
                const countLabel = totalCount === 1 ? (T.reviewSingular || 'Bewertung') : (T.reviewPlural || 'Bewertungen');
                breakdownContainer.querySelector('.reviews-breakdown__score').textContent = rating;
                const countOnly = totalCount.toLocaleString(document.documentElement.lang || 'de-DE') + ' ' + countLabel;
                breakdownContainer.querySelector('.reviews-breakdown__count').textContent = countOnly;

                if (starsEl) {
                    const starsContainer = breakdownContainer.querySelector('.reviews-breakdown__stars');
                    starsContainer.innerHTML = starsEl.outerHTML;
                    enforceTransparentDisabledStars();
                }
            }

            const updateBreakdownFromWidget = () => {
                const dropdown = document.querySelector(widgetSelector + ' .R-DropUpDownMenu');
                const ratingData = {};
                let foundData = false;

                if (dropdown) {
                    const items = dropdown.querySelectorAll('[class*="item"], [class*="Item"], li, div[role="option"]');

                    items.forEach(item => {
                        const text = item.textContent.trim();
                        const starMatch = text.match(/^(\d)\s*(?:Stern|★|star)/i);
                        const countMatch = text.match(/\(([\d.,\s]+)\)/);

                        if (starMatch && countMatch) {
                            const starRating = parseInt(starMatch[1]);
                            const count = parseInt(countMatch[1].replace(/[.,\s]/g, ''));
                            ratingData[starRating] = count;
                            foundData = true;
                        } else {
                            const starsContainer = item.querySelector('[class*="RatingStars"], [class*="stars"]');
                            if (starsContainer) {
                                const filledStars = starsContainer.querySelectorAll('[class*="100"], [class*="full"], .filled').length;
                                const countInText = text.match(/([\d.,]+)/g);
                                if (filledStars > 0 && countInText) {
                                    const count = parseInt(countInText[countInText.length - 1].replace(/[.,]/g, ''));
                                    if (count > 0) {
                                        ratingData[filledStars] = count;
                                        foundData = true;
                                    }
                                }
                            }
                        }
                    });
                }

                if (foundData) {
                    const total = Object.values(ratingData).reduce((a, b) => a + b, 0) || totalCount;

                    [5, 4, 3, 2, 1].forEach(star => {
                        const count = ratingData[star] || 0;
                        const percent = total > 0 ? (count / total) * 100 : 0;

                        const barEl = breakdownContainer.querySelector(`.reviews-breakdown__bar[data-rating="${star}"]`);
                        if (barEl) {
                            const fillEl = barEl.querySelector('.reviews-breakdown__fill');
                            if (fillEl) fillEl.setAttribute('style', `width: ${percent}% !important;`);
                            barEl.querySelector('.reviews-breakdown__value').textContent = count.toLocaleString(document.documentElement.lang || 'de-DE');
                        }
                    });
                }
                return foundData;
            };

            updateBreakdownFromWidget();
            syncBreakdownVisibility();
            return true;
        };

        const waitForBreakdown = () => {
            let attempts = 0;
            const maxAttempts = 10;
            
            const tryInit = () => {
                attempts++;
                if (initRatingBreakdown()) {
                    return true;
                }
                
                if (attempts < maxAttempts) {
                    setTimeout(tryInit, 300);
                } else {
                    const observer = new MutationObserver((mutations, obs) => {
                        if (initRatingBreakdown()) obs.disconnect();
                    });
                    const widgetContainer = document.querySelector(widgetSelector);
                    if (widgetContainer) {
                        observer.observe(widgetContainer, { childList: true, subtree: true });
                        setTimeout(() => observer.disconnect(), 5000);
                    }
                }
                return false;
            };
            
            setTimeout(tryInit, 500);
        };

        // Kick off mounting only after waitForFilters/waitForBreakdown are
        // defined: runPostMountInit calls them on mount success, and an
        // immediate mount would otherwise hit the const TDZ above.
        ensureWidgetMounted();
        // Mount as soon as polaris has run rather than on the next retry
        // tick; the bounded retry and the script watcher still cover a late
        // or consent-blocked script, and a failed load leaves them to it.
        polarisLoad.then(ensureWidgetMounted, () => {});
        window.addEventListener('load', ensureWidgetMounted, { once: true });
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                ensureWidgetMounted();
            }
        });

        const modalHost = getWidgetContainer();
        const modalDialog = modalHost ? modalHost.closest('modal-dialog') : null;
        if (modalDialog) {
            const modalObserver = new MutationObserver(() => {
                if (modalDialog.hasAttribute('open') && ensureWidgetMounted()) {
                    modalObserver.disconnect();
                }
            });

            modalObserver.observe(modalDialog, { attributes: true, attributeFilter: ['open'] });
        }

        const preventDuplicates = () => {
            const widgetContainer = document.querySelector(widgetSelector);
            if (!widgetContainer) return;
            const breakdowns = widgetContainer.querySelectorAll('.c-reviewsBreakdown, .reviews-breakdown');
            if (breakdowns.length > 1) {
                breakdowns.forEach((bd, idx) => {
                    if (idx > 0 && !bd.id.includes('breakdown')) {
                        bd.style.display = 'none';
                    }
                });
            }
            const contentLists = widgetContainer.querySelectorAll('.R-ContentList');
            if (contentLists.length > 1) {
                contentLists.forEach((list, idx) => {
                    if (idx > 0) {
                        list.style.display = 'none';
                    }
                });
            }
        };

        const transformRecommendSection = () => {
            const reviewItems = document.querySelectorAll(widgetSelector + ' .R-ContentList__item');
            let transformed = false;

            reviewItems.forEach(item => {
                item.querySelectorAll('.R-BadgeElement').forEach(nativeBadge => {
                    if (nativeBadge.classList.contains('reviews-native-checked')) return;
                    nativeBadge.classList.add('reviews-native-checked');
                    const badgeText = nativeBadge.textContent.toLowerCase();
                    if (badgeText.includes('empfehl') || badgeText.includes('recommend')) {
                        nativeBadge.style.display = 'none';
                    }
                });

                const attributeGroup = item.querySelector('.c-item__attributesGroup');
                if (!attributeGroup) return;

                const headingText = attributeGroup.querySelector('.R-TextHeading');
                if (!headingText) return;
                const heading = headingText.textContent.toLowerCase();
                if (!heading.includes('empfehl') && !heading.includes('recommend')) return;
                const valueEl = attributeGroup.querySelector('.R-TextBody--xxxs:not(.u-textRight--all)');
                if (!valueEl) return;
                const answer = valueEl.textContent.trim().toLowerCase();
                const isPositive = answer === 'ja' || answer === 'yes';

                if (!isPositive) {
                    item.querySelectorAll('.reviews-recommend-section').forEach(el => el.remove());
                    attributeGroup.classList.add('reviews-recommend-transformed');
                    return;
                }
                if (item.querySelector('.reviews-recommend-section')) return;
                attributeGroup.classList.add('reviews-recommend-transformed');

                const badge = document.createElement('div');
                badge.className = 'reviews-recommend-section';
                        badge.innerHTML = `
                    <div class="reviews-recommend-badge">
                        <span class="reviews-recommend-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" width="16" height="16">
                                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="6" d="M60,101.61L21.53,63.59s-6.16-5.48-6.16-16.43c0-11.92,8.62-22.88,24.01-22.88,15.39,0,20.62,12.57,20.62,12.57,0,0,5.23-12.57,20.62-12.57s24.01,10.95,24.01,22.88c0,10.95-6.16,16.43-6.16,16.43l-38.47,38.02Z"/>
                            </svg>
                        </span>
                        <span class="reviews-recommend-text">${(window.__reviewsIoFilterTranslations || {}).recommendBadge || 'Ich empfehle dieses Produkt'}</span>
                    </div>
                `;

                const authorDetails = item.querySelector('.c-meta__authorDetails');
                const dateContainer = item.querySelector('.R-TextBody--xxxs.u-textRight--all');

                if (authorDetails) {
                    const desktopBadge = badge.cloneNode(true);
                    desktopBadge.classList.add('reviews-recommend-desktop');
                    authorDetails.appendChild(desktopBadge);
                }

                if (dateContainer && dateContainer.parentElement) {
                    const mobileBadge = badge.cloneNode(true);
                    mobileBadge.classList.add('reviews-recommend-mobile');
                    dateContainer.parentElement.insertBefore(mobileBadge, dateContainer);
                }

                transformed = true;
            });

            return transformed;
        };

        const enableRecommend = false; // set to true to show "Ich empfehle dieses Produkt"

        // Force transparent disabled star color on all star containers
        const enforceTransparentDisabledStars = () => {
            document.querySelectorAll('.R-RatingStars, .R-RatingStars__stars').forEach(el => {
                el.style.setProperty('--common-star-disabled-color', 'transparent', 'important');
            });
        };

        const transformReviewLayout = () => {
            const reviewItems = document.querySelectorAll(widgetSelector + ' .R-ContentList__item');

            reviewItems.forEach(item => {
                const authorDetails = item.querySelector('.c-meta__authorDetails');
                if (!authorDetails) return;

                // Find unprocessed stars and badge (exclude anything already inside reviews-stars-badge-row)
                const starsEl = Array.from(item.querySelectorAll('.R-RatingStars')).find(el =>
                    !el.closest('.reviews-stars-badge-row')
                );
                const badgeEl = Array.from(item.querySelectorAll('.R-BadgeElement')).find(el =>
                    !el.closest('.reviews-stars-badge-row')
                );
                const nameEl = authorDetails.querySelector('.R-TextHeading:not(.reviews-name-desktop-only)');

                // Nothing new to process
                if (!starsEl && !badgeEl && !nameEl) return;

                // Get or create the stars+badge row
                let starsBadgeRow = authorDetails.querySelector('.reviews-stars-badge-row');
                if (!starsBadgeRow) {
                    starsBadgeRow = document.createElement('div');
                    starsBadgeRow.className = 'reviews-stars-badge-row';
                    authorDetails.appendChild(starsBadgeRow);
                }

                // Move stars into the row (not clone — cloned stars don't render icon fonts properly)
                if (starsEl) {
                    // Remove any previously moved stars from the row
                    starsBadgeRow.querySelectorAll('.R-RatingStars').forEach(el => el.remove());
                    // Stars should come first, so insert at the beginning
                    starsBadgeRow.insertBefore(starsEl, starsBadgeRow.firstChild);

                    // Immediately enforce transparent disabled stars
                    enforceTransparentDisabledStars();
                }

                // Move badge into the row
                if (badgeEl) {
                    starsBadgeRow.querySelectorAll('.R-BadgeElement').forEach(el => el.remove());
                    starsBadgeRow.appendChild(badgeEl);
                }

                // Hide the original name on mobile (we show it in bottom bar instead)
                if (nameEl) {
                    nameEl.classList.add('reviews-name-desktop-only');

                    const bottomBar = item.querySelector('.u-marginBottom--sm.u-flexGrow');
                    const updateMobileName = () => {
                        const text = nameEl.textContent.trim();
                        if (!text || !bottomBar) return;
                        const existingMobileName = bottomBar.querySelector('.reviews-reviewer-name-mobile');
                        if (existingMobileName) {
                            existingMobileName.textContent = text;
                        } else {
                            const mobileName = document.createElement('div');
                            mobileName.className = 'reviews-reviewer-name-mobile';
                            mobileName.textContent = text;
                            bottomBar.insertBefore(mobileName, bottomBar.firstChild);
                        }
                    };
                    updateMobileName();
                    new MutationObserver(updateMobileName).observe(nameEl, { childList: true, subtree: true, characterData: true });
                }
            });
        };

        const waitForRecommendSection = () => {
            preventDuplicates();
            if (enableRecommend) transformRecommendSection();
            transformReviewLayout();

            const observer = new MutationObserver(() => {
                preventDuplicates();
                if (enableRecommend) transformRecommendSection();
                transformReviewLayout();
            });
            const widgetContainer = document.querySelector(widgetSelector);
            if (widgetContainer) {
                observer.observe(widgetContainer, { childList: true, subtree: true });
            }
        };
        waitForRecommendSection();
        setTimeout(() => {
            preventDuplicates();
        }, 2000);

        // Debounced enforcement of transparent disabled stars
        let starEnforceTimer;
        const debouncedEnforceStars = () => {
            clearTimeout(starEnforceTimer);
            starEnforceTimer = setTimeout(enforceTransparentDisabledStars, 100);
        };

        enforceTransparentDisabledStars();
        setTimeout(enforceTransparentDisabledStars, 500);
        setTimeout(enforceTransparentDisabledStars, 1500);
        setTimeout(enforceTransparentDisabledStars, 3000);

        const starObserver = new MutationObserver(debouncedEnforceStars);
        const widgetEl = document.querySelector(widgetSelector);
        if (widgetEl) {
            starObserver.observe(widgetEl, { childList: true, subtree: true });
        }

    };
    // An inline widget (the reviews strip) sits far below the fold: request
    // polaris and mount only when it comes within ~1.5 viewports. The theme
    // editor mounts on DOMContentLoaded, as before.
    const mountOnApproach = () => {
        const container = document.querySelector(widgetSelector);
        if (designMode || !container) {
            initReviewsWidget();
            return;
        }
        whenApproaching(container, initReviewsWidget);
    };
    const whenParsed = (callback) => {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', callback, { once: true });
        } else {
            callback();
        }
    };
    if (config.lazyModal) {
    const lazyModal = document.getElementById(config.lazyModal);
    if (lazyModal) {
        if (lazyModal.hasAttribute('open')) {
            initReviewsWidget();
        } else {
            // ModalDialog.show() sets [open]; load polaris and mount on the
            // first open only (initReviewsWidget calls loadPolaris first).
            const openWatcher = new MutationObserver(() => {
                if (!lazyModal.hasAttribute('open')) return;
                openWatcher.disconnect();
                initReviewsWidget();
            });
            openWatcher.observe(lazyModal, { attributes: true, attributeFilter: ['open'] });
        }
    } else {
        // A renamed or missing modal must not leave the widget unmounted.
        console.warn('reviewsio-widget: modal #' + config.lazyModal + ' not found; mounting when it approaches.');
        whenParsed(mountOnApproach);
    }
    } else {
    whenParsed(mountOnApproach);
    }
    })();
  }

  document.querySelectorAll('script[data-reviewsio-config]').forEach((element) => {
    if (element.dataset.reviewsioStarted) return;
    element.dataset.reviewsioStarted = 'true';
    try {
      initReviewsIo(JSON.parse(element.textContent));
    } catch (error) {
      console.error('reviewsio-widget: bad config', error);
    }
  });
})();
